import {useEffect, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";

import {LocationProvider} from "snimf/location/provider";
import {useFormDirtyContext} from "base/form/provider";
import {FormUtil} from "base/form/utilities";

import {EntityForm} from "base/entity/components/form";
import Stack from "@mui/material/Stack";

const PermitExtensionForm = ({
    entity = null,
    permitId = null,
    onSubmit,
    onCancel = null,
    direction = "column",
    children,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const defaultFormValues = {
        id: FormUtil.getFormValue(entity?.id),
        code: FormUtil.getFormValue(entity?.code),
        on_date: FormUtil.getFormValue(entity?.on_date),
        application_date: FormUtil.getFormValue(entity?.application_date),
        status: FormUtil.getFormValue(entity?.status),
        is_total: FormUtil.getFormValue(entity?.is_total),
        types: FormUtil.getFormValue(entity?.types, []),
        felling: FormUtil.getFormValue(entity?.felling),
        felling_start_date: FormUtil.getFormValue(entity?.felling_start_date),
        felling_end_date: FormUtil.getFormValue(entity?.felling_end_date),
        felling_price: FormUtil.getFormValue(entity?.felling_price),
        cutting: FormUtil.getFormValue(entity?.cutting),
        cutting_start_date: FormUtil.getFormValue(entity?.cutting_start_date),
        cutting_end_date: FormUtil.getFormValue(entity?.cutting_end_date),
        cutting_price: FormUtil.getFormValue(entity?.cutting_price),
        clearing: FormUtil.getFormValue(entity?.clearing),
        clearing_start_date: FormUtil.getFormValue(entity?.clearing_start_date),
        clearing_end_date: FormUtil.getFormValue(entity?.clearing_end_date),
        clearing_area: FormUtil.getFormValue(entity?.clearing_area),
        clearing_price: FormUtil.getFormValue(entity?.clearing_price),
        transport: FormUtil.getFormValue(entity?.transport),
        transport_start_date: FormUtil.getFormValue(entity?.transport_start_date),
        transport_end_date: FormUtil.getFormValue(entity?.transport_end_date),
        transport_price: FormUtil.getFormValue(entity?.transport_price),
        technical_decision: FormUtil.getFormValue(entity?.technical_decision),
        is_signed: FormUtil.getFormValue(entity?.is_signed),
        fee_type: FormUtil.getFormValue(entity?.fee_type),
        fee_amount: FormUtil.getFormValue(entity?.fee_amount),
        comments: FormUtil.getFormValue(entity?.comments),
        technical_comments: FormUtil.getFormValue(entity?.technical_comments),
        invoice_sent: FormUtil.getFormValue(entity?.invoice_sent),
        extension_sent: FormUtil.getFormValue(entity?.extension_sent),
        extension_signed: FormUtil.getFormValue(entity?.extension_signed),
    };

    const formMethods = useForm({
        defaultValues: defaultFormValues,
        reValidateMode: "onSubmit",
    });

    const {
        formState: {isDirty},
    } = formMethods;

    const context = useFormDirtyContext();
    const setDirty = context?.setDirty ?? (() => {});

    useEffect(() => {
        setDirty(isDirty);
    }, [isDirty]);

    const handleFormSubmit = data => {
        setIsSubmitting(true);

        const updatedExtension = {
            id: FormUtil.getDataValue(data.id),
            code: FormUtil.getDataValue(data.code),
            on_date: FormUtil.getDataValue(data.on_date),
            application_date: FormUtil.getDataValue(data.application_date),
            status: FormUtil.getDataValue(data.status),
            is_total: FormUtil.getDataValue(data.is_total),
            types: FormUtil.getDataValue(data.types),
            felling: FormUtil.getDataValue(data.felling),
            felling_start_date: FormUtil.getDataValue(data.felling_start_date),
            felling_end_date: FormUtil.getDataValue(data.felling_end_date),
            felling_price: FormUtil.getDataValue(data.felling_price),
            cutting: FormUtil.getDataValue(data.cutting),
            cutting_start_date: FormUtil.getDataValue(data.cutting_start_date),
            cutting_end_date: FormUtil.getDataValue(data.cutting_end_date),
            cutting_price: FormUtil.getDataValue(data.cutting_price),
            clearing: FormUtil.getDataValue(data.clearing),
            clearing_start_date: FormUtil.getDataValue(data.clearing_start_date),
            clearing_end_date: FormUtil.getDataValue(data.clearing_end_date),
            clearing_area: FormUtil.getDataValue(data.clearing_area),
            clearing_price: FormUtil.getDataValue(data.clearing_price),
            transport: FormUtil.getDataValue(data.transport),
            transport_start_date: FormUtil.getDataValue(data.transport_start_date),
            transport_end_date: FormUtil.getDataValue(data.transport_end_date),
            transport_price: FormUtil.getDataValue(data.transport_price),
            technical_decision: FormUtil.getDataValue(data.technical_decision),
            is_signed: FormUtil.getDataValue(data.is_signed),
            fee_type: FormUtil.getDataValue(data.fee_type),
            fee_amount: FormUtil.getDataValue(data.fee_amount),
            comments: FormUtil.getDataValue(data.comments),
            technical_comments: FormUtil.getDataValue(data.technical_comments),
            invoice_sent: FormUtil.getDataValue(data?.invoice_sent),
            extension_sent: FormUtil.getDataValue(data?.extension_sent),
            extension_signed: FormUtil.getDataValue(data?.extension_signed),
            permit_id: FormUtil.getDataValue(permitId || entity?.permit?.id),
        };

        onSubmit(updatedExtension, success => {
            setIsSubmitting(false);
        });
    };

    return (
        <LocationProvider>
            <FormProvider {...formMethods}>
                <Stack spacing={1}>
                    <EntityForm
                        onSubmit={formMethods.handleSubmit(handleFormSubmit)}
                        onCancel={onCancel}
                        isFormDirty={isDirty}
                        isSubmitting={isSubmitting}
                        direction={direction}
                    >
                        {children}
                    </EntityForm>
                </Stack>
            </FormProvider>
        </LocationProvider>
    );
};

export default PermitExtensionForm;
